import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import LightbulbIcon from "@mui/icons-material/Lightbulb";
import QuizIcon from "@mui/icons-material/Quiz";
import TipsAndUpdatesIcon from "@mui/icons-material/TipsAndUpdates";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import RefreshIcon from "@mui/icons-material/Refresh";
import { getAiSummary } from "api/cansurvey/surveyApi";
import type { StructuredSurveySummary } from "types/survey";

// Parse summary content — try JSON first, fall back to null
const parseSummary = (content: string): StructuredSurveySummary | null => {
  try {
    const parsed = JSON.parse(content);
    if (parsed.keyFindings || parsed.questionInsights || parsed.recommendations) {
      return parsed as StructuredSurveySummary;
    }
    return null;
  } catch {
    return null;
  }
};

// Get priority chip color using theme tokens
const getPriorityColor = (
  priority: string
): "error" | "warning" | "success" | "default" => {
  switch (priority) {
    case "high":
      return "error";
    case "medium":
      return "warning";
    case "low":
      return "success";
    default:
      return "default";
  }
};

// Structured summary view component
const StructuredSurveySummaryView: React.FC<{
  summary: StructuredSurveySummary;
}> = ({ summary }) => {
  return (
    <Box>
      {/* Header with category and tags */}
      <Box display="flex" gap={1} flexWrap="wrap" mb={2}>
        <Chip
          label={
            summary.surveyCategory?.replace("-", " ").toUpperCase() || "SURVEY"
          }
          size="small"
          variant="outlined"
        />
        {summary.tags?.map((tag, index) => (
          <Chip key={index} label={tag} size="small" variant="outlined" />
        ))}
      </Box>

      {/* Overall Insight */}
      {summary.overallInsight && (
        <Typography variant="body1" fontWeight="bold" sx={{ mb: 2 }}>
          {summary.overallInsight}
        </Typography>
      )}

      {/* Response Overview */}
      {summary.responseOverview && (
        <Box mb={2}>
          <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
            <Typography variant="body2" color="text.secondary">
              <strong>{summary.responseOverview.totalResponses}</strong>{" "}
              response
              {summary.responseOverview.totalResponses !== 1 ? "s" : ""}{" "}
              analyzed
            </Typography>
            {summary.responseOverview.dateRange && (
              <Typography variant="body2" color="text.secondary">
                {summary.responseOverview.dateRange.earliest} &mdash;{" "}
                {summary.responseOverview.dateRange.latest}
              </Typography>
            )}
          </Stack>
          {summary.responseOverview.sourceBreakdown && (
            <Box display="flex" gap={0.5} flexWrap="wrap" mt={1}>
              {Object.entries(summary.responseOverview.sourceBreakdown).map(
                ([source, count]) => (
                  <Chip
                    key={source}
                    label={`${source}: ${count}`}
                    size="small"
                    variant="outlined"
                  />
                )
              )}
            </Box>
          )}
        </Box>
      )}

      <Divider sx={{ my: 2 }} />

      {/* Key Findings */}
      {summary.keyFindings?.length > 0 && (
        <Box mb={3}>
          <Box display="flex" alignItems="center" gap={1} mb={1}>
            <LightbulbIcon color="primary" />
            <Typography variant="subtitle1" fontWeight="bold">
              Key Findings
            </Typography>
          </Box>
          <List dense disablePadding>
            {summary.keyFindings.map((item, index) => (
              <ListItem key={index} sx={{ py: 0.2 }}>
                <ListItemIcon sx={{ minWidth: 24 }}>
                  <FiberManualRecordIcon
                    sx={{ fontSize: 8, color: "text.secondary" }}
                  />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Box
                      display="flex"
                      alignItems="center"
                      gap={1}
                      flexWrap="wrap"
                    >
                      <Typography
                        variant="body2"
                        component="span"
                        fontWeight="medium"
                      >
                        {item.finding}
                      </Typography>
                      <Chip
                        label={item.importance}
                        size="small"
                        color={getPriorityColor(item.importance)}
                        variant="outlined"
                        sx={{ height: 20, fontSize: "0.7rem" }}
                      />
                    </Box>
                  }
                />
              </ListItem>
            ))}
          </List>
        </Box>
      )}

      <Divider sx={{ my: 2 }} />

      {/* Question Insights */}
      {summary.questionInsights?.length > 0 && (
        <Box mb={3}>
          <Box display="flex" alignItems="center" gap={1} mb={1}>
            <QuizIcon color="secondary" />
            <Typography variant="subtitle1" fontWeight="bold">
              Question Insights
            </Typography>
          </Box>
          {summary.questionInsights.map((item, index) => (
            <Box key={index} sx={{ mb: 2, pl: 1 }}>
              <Typography
                variant="body2"
                fontWeight="bold"
                sx={{ mb: 0.5 }}
              >
                {item.question}
              </Typography>
              {item.topAnswers?.length > 0 && (
                <Box display="flex" gap={0.5} flexWrap="wrap" mb={0.5}>
                  {item.topAnswers.map((answer, i) => (
                    <Chip
                      key={i}
                      label={answer}
                      size="small"
                      variant="outlined"
                    />
                  ))}
                </Box>
              )}
              <Typography variant="body2" color="text.secondary">
                {item.insight}
              </Typography>
            </Box>
          ))}
        </Box>
      )}

      <Divider sx={{ my: 2 }} />

      {/* Recommendations */}
      {summary.recommendations?.length > 0 && (
        <Box mb={2}>
          <Box display="flex" alignItems="center" gap={1} mb={1}>
            <TipsAndUpdatesIcon sx={{ color: "warning.main" }} />
            <Typography variant="subtitle1" fontWeight="bold">
              Recommendations
            </Typography>
          </Box>
          <List dense disablePadding>
            {summary.recommendations.map((item, index) => (
              <ListItem key={index} sx={{ py: 0.2 }}>
                <ListItemIcon sx={{ minWidth: 24 }}>
                  <FiberManualRecordIcon
                    sx={{ fontSize: 8, color: "text.secondary" }}
                  />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Box
                      display="flex"
                      alignItems="center"
                      gap={1}
                      flexWrap="wrap"
                    >
                      <Typography
                        variant="body2"
                        component="span"
                        fontWeight="medium"
                      >
                        {item.recommendation}
                      </Typography>
                      <Chip
                        label={item.priority}
                        size="small"
                        color={getPriorityColor(item.priority)}
                        variant="outlined"
                        sx={{ height: 20, fontSize: "0.7rem" }}
                      />
                    </Box>
                  }
                />
              </ListItem>
            ))}
          </List>
        </Box>
      )}
    </Box>
  );
};

interface AiSummaryDialogProps {
  open: boolean;
  surveyId: number;
  surveyTitle: string;
  onClose: () => void;
}

const AiSummaryDialog: React.FC<AiSummaryDialogProps> = ({
  open,
  surveyId,
  surveyTitle,
  onClose,
}) => {
  const [loading, setLoading] = useState(false);
  const [summaryContent, setSummaryContent] = useState("");
  const [error, setError] = useState("");

  const fetchSummary = useCallback(
    async (force: boolean = false) => {
      setLoading(true);
      setSummaryContent("");
      setError("");

      try {
        const response = await getAiSummary(surveyId, force);
        if (response.summary) {
          setSummaryContent(response.summary);
        } else {
          setError(
            "AI Summary is not available at this moment. Please try again later."
          );
        }
      } catch (err) {
        console.error("[AiSummaryDialog] Failed to generate summary:", err);
        setError(
          "AI Summary is not available at this moment. Please try again later."
        );
      } finally {
        setLoading(false);
      }
    },
    [surveyId]
  );

  useEffect(() => {
    if (open) {
      fetchSummary();
    }
  }, [open, fetchSummary]);

  const handleRegenerate = () => {
    fetchSummary(true);
  };

  const handleClose = () => {
    setSummaryContent("");
    setError("");
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="flex-start"
        >
          <Box>
            <Typography
              variant="h5"
              fontWeight="bold"
              sx={{ textTransform: "uppercase", letterSpacing: 1 }}
            >
              {surveyTitle}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              AI Summary
            </Typography>
          </Box>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            py={4}
          >
            <CircularProgress />
            <Typography sx={{ mt: 2 }} color="text.secondary">
              Generating summary... This may take up to a minute.
            </Typography>
          </Box>
        ) : error ? (
          <Typography color="error">{error}</Typography>
        ) : (
          <Box>
            {(() => {
              const structured = parseSummary(summaryContent);
              if (structured) {
                return <StructuredSurveySummaryView summary={structured} />;
              }
              return (
                <Box sx={{ whiteSpace: "pre-wrap", fontFamily: "inherit" }}>
                  {summaryContent}
                </Box>
              );
            })()}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ justifyContent: "space-between", px: 3, py: 2 }}>
        <Box display="flex" alignItems="center" gap={0.5}>
          <AutoAwesomeIcon
            sx={{ fontSize: 14, color: "text.disabled" }}
          />
          <Typography
            variant="caption"
            sx={{
              color: "text.disabled",
              fontSize: "0.7rem",
              fontStyle: "italic",
            }}
          >
            AI Insights powered by CANai
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          {summaryContent && !error && (
            <Button
              onClick={handleRegenerate}
              variant="outlined"
              size="small"
              startIcon={
                loading ? (
                  <CircularProgress size={16} />
                ) : (
                  <RefreshIcon />
                )
              }
              disabled={loading}
            >
              {loading ? "Generating..." : "Regenerate"}
            </Button>
          )}
          <Button onClick={handleClose} variant="contained">
            Close
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};

export default AiSummaryDialog;
